import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.reducer';
import { FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Observable, ReplaySubject, Subscription, filter } from 'rxjs';
import * as DeviceActions from 'src/app/store/device/device.actions';
import moment from 'moment';
import { LANGUAGES } from 'src/app/conf/languages';
import { TIMEZONES } from 'src/app/conf/timezones';
import { DeviceStatus } from 'src/app/model/device-status';
import { Lang } from 'src/app/model/lang';
import { Permission, PermittedCommand, GroupedPermittedCommandsItem, PermittedThs, PermissionThs } from 'src/app/model/permission';
import { CustomValidators } from 'src/app/validators/custom.validators';
import { selectPermission, selectPermissionsAdded, selectPermissionsAdding, selectPermissionsEdited, selectPermissionsEditing, selectPermittedCommands, selectPermittedThs } from 'src/app/store/permissions/permissions.selector';
import { selectUser } from 'src/app/store/user/user.selector';
import { User } from 'src/app/model/user';
import { Site } from 'src/app/model/site';
import { selectCurrentSite } from 'src/app/store/site/site.selector';
import { selectDevicesStatus } from 'src/app/store/device/device.selector';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import * as PermissionActions from 'src/app/store/permissions/permissions.actions';
import { StandardDesignParameters, StandardDynamicComponent, StandardNewValidator, StandardNewValidatorStepTabs, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { PERMISSION_DEFAULT_END, PERMISSION_DEFAULT_START } from 'src/app/conf/permissions';
import { RBE_24_COMMAND_ID_AUTOMATION_1, RBE_24_COMMAND_ID_AUTOMATION_2, RBE_24_COMMAND_ID_AUTOMATION_3, RBE_24_COMMAND_ID_AUTOMATION_4 } from 'src/app/conf/rbe24230';
import * as NotificationsActions from 'src/app/store/notifications/notifications.actions';
import { Notification, NotificationLevel } from 'src/app/model/notification';
import { TranslateService } from '@ngx-translate/core';
import { isMtmacModel, isMultiIo, isRbe24230 } from 'src/app/conf/device-models';
import { Device } from 'src/app/model/device';
import { CheckboxChangeEvent } from 'primeng/checkbox';
import { normalizeEmailControl } from 'src/app/modules/shared/utils/strip-email-spaces';

@Component({
	templateUrl: './permission.dialog.component.html',
})

export class PermissionDialogComponent implements OnInit, OnDestroy {
	adding: boolean;
	adding$: Observable<boolean | undefined>;
	addingSub: Subscription | undefined;
	editing$: Observable<boolean | undefined>;
	editingSub: Subscription | undefined;
	currentUser$: Observable<User | undefined>;
	currentUserSub: Subscription | undefined;
	site$: Observable<Site | undefined>;
	siteSub: Subscription | undefined;
	devicesStatus$: Observable<DeviceStatus[] | undefined>;
	devicesStatusSub: Subscription | undefined;
	added$: Observable<boolean | undefined>;
	addedSub: Subscription | undefined;
	edited$: Observable<boolean | undefined>;
	editedSub: Subscription | undefined;
	permittedCommand$: Observable<PermittedCommand[] | undefined>;
	permittedCommandSub: Subscription | undefined;
	permission$: Observable<Permission | undefined>;
	permissionSub: Subscription | undefined;
	permittedThs$: Observable<PermittedThs[] | undefined>;
	permittedThsSub: Subscription | undefined;

	permission: Permission;
	editMode = false;

	currentSite: Site;
	permissionForm: FormGroup;

	languages: Lang[] = LANGUAGES;
	flagImagePath = './assets/images/flags/';
	flagImageFormat = '.svg';

	timezones = TIMEZONES;
	tzSub: Subscription;

	timezoneFilterCtrl = new FormControl('');
	filteredTimezone: any[];
	filteredTimezones: ReplaySubject<string[]> = new ReplaySubject<string[]>(1);

	permittedCommands: PermittedCommand[] = [];
	groupedCommands: GroupedPermittedCommandsItem[] = [];
	devicesStatus: DeviceStatus[] = [];
	commandsAutomations: boolean = true;
	permittedCommandsDevicesOffline: boolean = false;
	permittedThs: PermittedThs[] = [];
	devicesCommandsThs: boolean = false;
	permittedThsOfflineOrQeDisable: boolean = false;

	currentUserLanguage: string;
	saving = false;

	activeIndex: number = 0;
	minStartDate: Date;
	minEndDate: Date;

	mismatchTzSitePermission: boolean = false;

	// design
	screenWidth: number;
	MAX_MD_WIDTH = StandardDesignParameters.MAX_MD_WIDTH;

	constructor(
		private fb: FormBuilder,
		private store: Store<AppState>,
		private ref: DynamicDialogRef,
		private translate: TranslateService,
		public config: DynamicDialogConfig
	) {
		this.initSelectors();
	}

	initSelectors(): void {
		// Store selectors
		this.adding$ = this.store.select(selectPermissionsAdding);
		this.editing$ = this.store.select(selectPermissionsEditing);
		this.currentUser$ = this.store.select(selectUser);
		this.site$ = this.store.select(selectCurrentSite);
		this.devicesStatus$ = this.store.select(selectDevicesStatus);
		this.added$ = this.store.select(selectPermissionsAdded);
		this.edited$ = this.store.select(selectPermissionsEdited);
		this.permission$ = this.store.select(selectPermission);
		this.permittedCommand$ = this.store.select(selectPermittedCommands);
		this.permittedThs$ = this.store.select(selectPermittedThs);
	}

	ngOnInit(): void {
		if (this.config.data.permission) { // edit permission
			this.editMode = true;
			this.permission = { ...this.config.data.permission };
			this.store.dispatch(PermissionActions.getPermission({ permissionId: this.permission.Id }));
			// set minDates selection possibile
			this.minStartDate = new Date(PERMISSION_DEFAULT_START); // minStartPermissionEdit = today
			this.minEndDate = new Date(PERMISSION_DEFAULT_START); // minEndPermissionEdit = today
			// check and set new permission dates for editPermission
			this.permission.Start = this.parseDateTimeFromTimezone(this.permission.Start, this.permission.Tz);
			if (this.permission.Start && this.minStartDate && this.permission.Start.getTime() < this.minStartDate.getTime()) {
				// set permission.Start as today because date saved before is now passed
				this.permission.Start = new Date(PERMISSION_DEFAULT_START);
			}
			this.permission.End = this.parseDateTimeFromTimezone(this.permission.End, this.permission.Tz);
			if (this.permission.End && this.minEndDate && this.permission.End.getTime() < this.minEndDate.getTime()) {
				// set permission.End as today + 7 days because date saved before is now passed
				this.permission.End = new Date(PERMISSION_DEFAULT_END);
			}
		} else { // add permission
			this.permission = new Permission();
			this.minStartDate = new Date(PERMISSION_DEFAULT_START); // minStartPermissionAdd = today
			this.minEndDate = new Date(PERMISSION_DEFAULT_START); // minEndPermissionAdd = today
		}

		this.currentUserSub = this.currentUser$.subscribe(currentUser => {
			this.currentUserLanguage = currentUser.Language;
			this.buildForm();
		});

		this.subscribeAll();

		this.onResize();

		StandardNewValidator.setFirstCompile(true);

		StandardNewValidatorStepTabs.firstTabfields = [];
		StandardNewValidatorStepTabs.secondTabfields = [];
		StandardNewValidatorStepTabs.thirdTabfields = [];

		StandardNewValidatorStepTabs.pushFieldOnTab(this.f.name, 0);
		StandardNewValidatorStepTabs.pushFieldOnTab(this.f.email, 0);
		StandardNewValidatorStepTabs.pushFieldOnTab(this.f.description, 0);
		StandardNewValidatorStepTabs.pushFieldOnTab(this.f.language, 0);
		StandardNewValidatorStepTabs.pushFieldOnTab(this.f.timezone, 1);
		StandardNewValidatorStepTabs.pushFieldOnTab(this.f.startDate, 1);
		StandardNewValidatorStepTabs.pushFieldOnTab(this.f.startTime, 1);
		StandardNewValidatorStepTabs.pushFieldOnTab(this.f.endDate, 1);
		StandardNewValidatorStepTabs.pushFieldOnTab(this.f.endTime, 1);
		StandardNewValidatorStepTabs.pushFieldOnTab(this.f.selCommands, 2);
		StandardNewValidatorStepTabs.pushFieldOnTab(this.f.selThs, 2);
		StandardNewValidatorStepTabs.pushFieldOnTab(this.f.pincode, 2);
		StandardNewValidatorStepTabs.pushFieldOnTab(this.f.rfid, 2);
	}

	subscribeAll() {
		this.siteSub = this.site$.subscribe((currentSite) => {
			this.currentSite = currentSite;
			if (currentSite.Devices.length > 0) {
				const deviceIds: number[] = currentSite.Devices.map(d => d.Id);
				this.store.dispatch(DeviceActions.getDevicesStatus({ deviceIds }));
			} else {
				this.devicesStatus = [];
			}
			this.mismatchTimezoneSitePermission();
		});
		this.devicesStatusSub = this.devicesStatus$.subscribe(
			devicesStatus => {
				this.devicesStatus = devicesStatus;
				this.createGroupPermittedCommands();
				this.disableListThs();
			}
		);
		// valid just for edit permission -> triggered after dispatch getPermission({ permissionId })
		this.permissionSub = this.permission$.pipe(filter(cmd => !!cmd)).subscribe(permission => {
			this.f.selCommands.setValue(this.selectCommands(permission.Commands));
			this.disableListCommand();
			if (permission.Ths.length > 0) {
				this.devicesCommandsThs = true;
			}
			this.f.selThs.setValue(this.selectThs(permission.Ths));
			this.disableListThs();
			this.updateSelCommandsThsValidators();
		});
		this.permittedThsSub = this.permittedThs$.subscribe(permittedThs => {
			this.permittedThs = permittedThs;
			if (permittedThs.length > 0) {
				this.devicesCommandsThs = true;
			}
			this.disableListThs();
			this.updateSelCommandsThsValidators();
		});
		this.addingSub = this.adding$.subscribe(flag => {
			this.saving = flag;
		});
		this.editingSub = this.editing$.subscribe(flag => {
			this.saving = flag;
		});
		this.editedSub = this.edited$.pipe(filter(ed => !!ed)).subscribe(() => {
			this.closeDialog();
		});
		this.addedSub = this.added$.pipe(filter(ed => !!ed)).subscribe(() => {
			this.closeDialog();
		});
	}

	ngOnDestroy(): void {
		this.currentUserSub?.unsubscribe();
		this.siteSub?.unsubscribe();
		this.devicesStatusSub?.unsubscribe();
		this.editedSub?.unsubscribe();
		this.addedSub?.unsubscribe();
		this.addingSub?.unsubscribe();
		this.editingSub?.unsubscribe();
		this.tzSub?.unsubscribe();
		this.permittedCommandSub?.unsubscribe();
		this.permissionSub?.unsubscribe();
		this.permittedThsSub?.unsubscribe();
	}

	someCommandsSelected(): boolean {
		return this.f.selCommands.value && (this.f.selCommands.value.length > 0);
	}

	buildForm(): void {
		this.permissionForm = new FormGroup({
			'name': new FormControl(this.permission.Name, Validators.required),
			'email': new FormControl({ value: this.permission.Email, disabled: this.editMode }, [Validators.email]),
			'description': new FormControl(this.permission.Description, Validators.maxLength(100)),
			'language': new FormControl(this.currentUserLanguage, [Validators.required]),
			'timezone': new FormControl(this.permission.Tz, Validators.required), // currentSite.Timezone doesn't exists here, we are before subscribeAll()
			'startDate': new FormControl(moment(this.permission.Start).toDate(), Validators.required),
			'startTime': new FormControl(this.getTime(this.permission.Start), Validators.required),
			'endDate': new FormControl(moment(this.permission.End).toDate(), Validators.required),
			'endTime': new FormControl(this.getTime(this.permission.End), Validators.required),
			'selCommands': new FormControl(''),
			'selThs': new FormControl(''),
			'pincode': new FormControl(this.permission.PinCode, [Validators.minLength(5),
			Validators.maxLength(5)]),
			'rfid': new FormControl(this.permission.RfidQeCode, Validators.maxLength(12)),
			'pincodeRandom': new FormControl(this.permission.RandomPincode),
			'enablePincodeDevices': new FormControl(this.permission.EnablePincodeDevices),
			'sendPincodeRbe': new FormControl(this.permission.SendPincodeRbe),
			'sendPincodeMtma': new FormControl(this.permission.SendPincodeMtma),
			'enableRfid': new FormControl(this.permission.EnableRfid),
			'sendTrasponderRbe': new FormControl(this.permission.SendRfidRbe),
			'sendTrasponderMtma': new FormControl(this.permission.SendRfidMtma),
			'sendMail': new FormControl(this.permission.SendMail),
			'sendAccessMail': new FormControl(this.permission.SendAccessMail),
		});
		this.permissionForm.addValidators(CustomValidators.checkCorrectDateSequence('startDate', 'startTime', 'endDate', 'endTime'));
		if (this.editMode) {
			if (this.permission.SendPincodeRbe === true || this.permission.SendPincodeMtma === true) {
				this.permissionForm.controls['enablePincodeDevices'].setValue(true);
			} else { // we saved false for Rbe and Mtma
				this.permissionForm.controls['enablePincodeDevices'].setValue(false);
			}
			this.permissionForm.controls['language'].setValue(this.permission.CustomerLanguage);
		} else { // adding start with true switch and true checkboxes
			this.permissionForm.controls['enablePincodeDevices'].setValue(true);
		}
		if (this.permission.RfidQeCode !== null && this.permission.RfidQeCode !== undefined && this.permission.RfidQeCode !== '') {
			this.permissionForm.controls['enableRfid'].setValue(true);
		} else {
			this.permissionForm.controls['enableRfid'].setValue(false);
		}
		this.checkEditableMailFlags();
		this.onSelectedPincodeRandom();
		this.onSelectedEnablePincodeToDevice();
		this.onSelectedEnableTagcodeToDevice();
		this.filteredTimezones.next(this.timezones.slice());
		this.tzSub = this.timezoneFilterCtrl.valueChanges.subscribe(() => this.filterTimezones());
		this.permittedCommandSub = this.permittedCommand$.subscribe(permittedCommands => {
			this.permittedCommands = permittedCommands;
			this.createGroupPermittedCommands();
		});
		this.f.selCommands.valueChanges.subscribe(() => this.updateSelCommandsThsValidators());
		this.f.selThs.valueChanges.subscribe(() => this.updateSelCommandsThsValidators());
	}

	get f(): any {
		return this.permissionForm?.controls;
	}

	canSwitchTab(): boolean {
		return StandardNewValidatorStepTabs.canSwitchTab(this.activeIndex);
	}

	changeActiveTabClick(index: number) {
		// if (this.canSwitchTab()) {
		this.activeIndex = index;
	}

	filterTimezone(event) {
		const filtered: any[] = [];
		const query = event.query;
		if (this.timezones) {
			for (let i = 0; i < this.timezones.length; i++) {
				const timezone = this.timezones[i];
				if (timezone.toLowerCase().indexOf(query.toLowerCase()) >= 0) {
					filtered.push(timezone);
				}
			}
		}

		this.filteredTimezone = filtered;
	}

	// permission.Commands have the Id field, while permittedCommands (used for the groupedComands list) have no Id
	selectCommands(commands: PermittedCommand[]): PermittedCommand[] {
		const cmds: PermittedCommand[] = [];
		commands.forEach(c => {
			const cmd = this.permittedCommands.find(pc => pc.DeviceId === c.DeviceId && pc.CommandId === c.CommandId);
			if (cmd) {
				cmds.push(cmd);
			}
		});
		return cmds;
	}

	selectThs(permissionThs: PermissionThs[]): PermittedThs[] {
		const thsSel: PermittedThs[] = [];
		permissionThs.forEach(permissionTh => {
			const thSel = this.permittedThs.find(permTh => permTh.Id === permissionTh.DeviceId);
			if (thSel) {
				thsSel.push(thSel);
			}
		});
		return thsSel;
	}

	createGroupPermittedCommands() {
		this.commandsAutomations = (this.permittedCommands && this.permittedCommands.length < 1) ? false : true;
		this.groupedCommands = this.groupPermittedCommands(this.permittedCommands);
		this.disableListCommand();
	}

	groupPermittedCommands(permittedCommands: PermittedCommand[]): GroupedPermittedCommandsItem[] {
		const cmds: GroupedPermittedCommandsItem[] = [];
		permittedCommands.forEach((c) => {
			if (this.devicesStatus.length > 0) {
				const device = this.devicesStatus.find(dd => dd.Id === c.DeviceId);
				if (device !== undefined) {
					if (device.Online === true) {
						this.permittedCommandsDevicesOffline = false;
					} else {
						this.permittedCommandsDevicesOffline = true;
					}
					// in add and edit: we add commands also if deviceRoot is offline
					// if deviceRoot is offline we Invalid commands (we cannot disable/enable)
					let cmd = cmds.find(gc => gc.DeviceName === c.DeviceName);
					if (!cmd) {
						cmd = new GroupedPermittedCommandsItem(c.DeviceName);
						cmds.push(cmd);
					}
					cmd.items.push(c);
				}
			}
		});
		return cmds;
	}

	disableListThs(): void {
		this.permittedThs.forEach((th) => {
			if (this.devicesStatus.length > 0) {
				const device = this.devicesStatus.find(dd => dd.Id === th.Id);
				if (device !== undefined) {
					if (device.Online !== true || th.QeEnable !== true) { // at least one TH is offline or QE disable
						this.permittedThsOfflineOrQeDisable = true; // show alert tooltip
						th.Inactive = true; // invalid th selection
					}
				}
			}
		});
	}

	disableListCommand(): void {
		this.groupedCommands.forEach(group => {
			for (const i of group.items) {
				i.Inactive = this.disabledCommandsAutomations(i);
			}
		});
	}

	disabledCommandsAutomations(command: PermittedCommand): boolean {
		let disable = false;
		const device = this.currentSite.Devices.find(dev => dev.Id === command.DeviceId);
		const deviceRoot = this.currentSite.Devices.find(dev => dev.Id === device.ParentId);
		if (deviceRoot) {
			const deviceRootStatus = this.devicesStatus.find(dd => dd.Id === deviceRoot.Id);
			if (deviceRootStatus && !deviceRootStatus?.Online) {
				return true;
			}
		}
		if (this.isMTMAConnect(device.ModelId) || (
			deviceRoot !== null && this.isMTMAConnect(deviceRoot.ModelId)) && !this.isCommandRbe24230(command.CommandId)
		) {
			disable = this.checkSelectedCommandMtma(command, device, deviceRoot);
		}
		if (command.CommandId < 256) {
			return disable;
		} else {
			return this.checkSelectedCommandAutomation(command) || disable;
		}
	}

	checkSelectedCommandAutomation(command: PermittedCommand): boolean {
		if (this.f.selCommands.value !== undefined && this.f.selCommands.value.length > 0) {
			for (const c of this.f.selCommands.value) {
				if (RBE_24_COMMAND_ID_AUTOMATION_1.includes(c.CommandId) && RBE_24_COMMAND_ID_AUTOMATION_1.includes(command.CommandId)
					&& c.DeviceId === command.DeviceId && c.CommandId !== command.CommandId) {
					return true;
				}
				if (RBE_24_COMMAND_ID_AUTOMATION_2.includes(c.CommandId) && RBE_24_COMMAND_ID_AUTOMATION_2.includes(command.CommandId)
					&& c.DeviceId === command.DeviceId && c.CommandId !== command.CommandId) {
					return true;
				}
				if (RBE_24_COMMAND_ID_AUTOMATION_3.includes(c.CommandId) && RBE_24_COMMAND_ID_AUTOMATION_3.includes(command.CommandId)
					&& c.DeviceId === command.DeviceId && c.CommandId !== command.CommandId) {
					return true;
				}
				if (RBE_24_COMMAND_ID_AUTOMATION_4.includes(c.CommandId) && RBE_24_COMMAND_ID_AUTOMATION_4.includes(command.CommandId)
					&& c.DeviceId === command.DeviceId && c.CommandId !== command.CommandId) {
					return true;
				}
			}
		}
		return false;
	}

	checkSelectedCommandMtma(command: PermittedCommand, device: Device, deviceRoot: Device): boolean {
		let cont = 0;
		if (this.f.selCommands.value !== undefined && this.f.selCommands.value.length > 0) {
			for (const c of this.f.selCommands.value) {
				const deviceCicle = this.currentSite.Devices.find(dev => dev.Id === c.DeviceId);
				const deviceRootCicle = this.currentSite.Devices.find(dev => dev.Id === deviceCicle.ParentId);
				// COUNT IF
				// SAME DEVICE AND DIFFERENT COMMAND
				// SAME PARENT ROOT AND SAME OR DIFFERENT COMMAND
				// NOT IF COMMNAD RBE 24230
				if ((c.DeviceId === command.DeviceId || (deviceRoot !== undefined && deviceRootCicle !== undefined &&
					deviceRoot.Id === deviceRootCicle.Id)) &&
					(c.CommandId !== command.CommandId || (c.CommandId === command.CommandId && c.DeviceId !== command.DeviceId)) &&
					!this.isCommandRbe24230(c.CommandId)) {
					cont++;
				}
			}
		}
		if (cont >= 2) {
			return true;
		}
		return false;
	}

	isCommandRbe24230(command: number): boolean {
		if (command >= 144 && command <= 147) { // RBE OUT
			return true;
		}
		if (command >= 256 && command <= 259) { // RBE AUT1
			return true;
		}
		if (command >= 260 && command <= 263) { // RBE AUT2
			return true;
		}
		if (command >= 264 && command <= 267) { // RBE AUT3
			return true;
		}
		if (command >= 268 && command <= 271) { // RBE AUT4
			return true;
		}
		return false;
	}

	getFormValidationErrors(): boolean {
		let atLeastOneError = false;
		Object.keys(this.permissionForm.controls).forEach(key => {
			const controlErrors: ValidationErrors = this.permissionForm.get(key).errors;
			if (controlErrors != null) {
				/* Object.keys(controlErrors).forEach(keyError => {
					console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
				}); */
				atLeastOneError = true;
			}
		});
		return atLeastOneError;
	}

	customSameDayValidatorErrors(): boolean {
		// se sono in questa funzione ho tutti e quattro i campi valorizzati se no mi sarei fermato a getFormValidationErrors
		const startDate = moment(this.f.startDate.value).format('YYYY-MM-DD');
		let startTime;
		if (typeof this.f.startTime.value === 'string') {
			startTime = 'T' + this.f.startTime.value + ':00';
		} else {
			startTime = moment(this.f.startTime.value).format('THH:mm:00');
		}
		const endDate = moment(this.f.endDate.value).format('YYYY-MM-DD');
		let endTime;
		if (typeof this.f.endTime.value === 'string') {
			endTime = 'T' + this.f.endTime.value + ':00';
		} else {
			endTime = moment(this.f.endTime.value).format('THH:mm:00');
		}
		if (startDate === endDate) {
			startTime = moment(startTime, 'HH:mm:ss');
			endTime = moment(endTime, 'HH:mm:ss');
			if (startTime.isSameOrAfter(endTime)) {
				this.permissionForm.controls["startTime"].setErrors({ required: true });
				this.permissionForm.controls["endTime"].setErrors({ required: true });
				this.f.startTime.value = '';
				this.f.endTime.value = '';
				const notification = new Notification("INSERT_NOT_VALID", this.translate.instant("TAG_DATE_WARNING_INVALID"), NotificationLevel.Error, '');
				this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
				return true;
			}
		}
		return false;
	}

	checkNotNewExpiredPermission(): boolean {
		const endDate = moment(this.f.endDate.value).format('YYYY-MM-DD');
		let endTime;
		if (typeof this.f.endTime.value === 'string') {
			endTime = 'T' + this.f.endTime.value + ':00';
		} else {
			endTime = moment(this.f.endTime.value).format('THH:mm:00');
		}

		const endInstant = this.combineDateTimeInTimezone(endDate, endTime, this.getPermissionTimezone());
		if (endInstant.getTime() <= Date.now()) {
			this.permissionForm.controls["endTime"].setErrors({ required: true });
			this.f.endTime.value = '';
			const notificationExpire = new Notification("INSERT_NOT_VALID", this.translate.instant("NEW_EXPIRED_PERMISSION"), NotificationLevel.Error, '');
			this.store.dispatch(NotificationsActions.addNotification({ notification: notificationExpire, toastOnly: true }));
			return true;
		}
		return false;
	}

	enableConfirmButtons(): boolean {
		return this.activeIndex === 2;
	}

	mismatchTimezoneSitePermission(): void {
		if (this.currentSite !== undefined && this.currentSite !== null) { // subscriber listener
			if (this.currentSite.Timezone === undefined || this.currentSite.Timezone === null) {
				// add or edit -> alert timezone not set site
				// is a just in case, because we are not able to open addPermission dialog without timezone site setted
				this.mismatchTzSitePermission = true;
				this.permissionForm.controls['timezone'].setErrors({ required: true });
				// this.permissionForm.controls['timezone'].disable();
				return;
			}
			if (!this.editMode) { // add mode
				this.permissionForm.controls['timezone'].setValue(this.currentSite.Timezone);
				this.permissionForm.controls['timezone'].disable();
				return;
			} else { // edit mode
				if (this.permissionForm.value.timezone !== undefined && this.permissionForm.value.timezone !== null) { // check mismatch timezones
					this.mismatchTzSitePermission = (this.currentSite.Timezone !== this.permissionForm.value.timezone);
					this.permissionForm.controls['timezone'].disable();
				} else { // permission edit timezone not set (just in case, couldn't happen because is a required field)
					this.mismatchTzSitePermission = true;
					if (this.currentSite.Timezone !== undefined && this.currentSite.Timezone !== null) { // auto set with timezone site
						this.permissionForm.controls['timezone'].setValue(this.currentSite.Timezone);
						this.permissionForm.controls['timezone'].disable();
					} else { // no timezones (site and permission) -> required
						this.permissionForm.controls['timezone'].setErrors({ required: true });
						// this.permissionForm.controls['timezone'].disable();
					}
				}
			}
		}
	}

	submit(): void {
		StandardNewValidator.setFirstCompile(false);
		normalizeEmailControl(this.f.email);
		this.updateSelCommandsThsValidators();
		if (!this.getFormValidationErrors()) {
			if (!this.customSameDayValidatorErrors() && !this.checkNotNewExpiredPermission()) {
				this.save();
			}
		}
	}

	private hasSelection(value: unknown): boolean {
		return Array.isArray(value) && value.length > 0;
	}

	updateSelCommandsThsValidators(): void {
		if (!this.f?.selCommands || !this.f?.selThs) {
			return;
		}

		const hasCommands = this.hasSelection(this.f.selCommands.value);
		const hasThs = this.hasSelection(this.f.selThs.value);

		if (hasCommands || hasThs) {
			this.f.selCommands.setErrors(null);
			this.f.selThs.setErrors(null);
			return;
		}

		// both empty: require at least one available selection type
		if (this.commandsAutomations) {
			this.f.selCommands.setErrors({ required: true });
		} else {
			this.f.selCommands.setErrors(null);
		}
		if (this.devicesCommandsThs) {
			this.f.selThs.setErrors({ required: true });
		} else {
			this.f.selThs.setErrors(null);
		}
	}

	save(): void {
		const startDate = moment(this.f.startDate.value).format('YYYY-MM-DD');
		let startTime = ''
		if (typeof this.f.startTime.value === 'string') {
			startTime = 'T' + this.f.startTime.value + ':00';
		} else {
			startTime = moment(this.f.startTime.value).format('THH:mm:00');
		}
		const endDate = moment(this.f.endDate.value).format('YYYY-MM-DD');
		let endTime = ''
		if (typeof this.f.endTime.value === 'string') {
			endTime = 'T' + this.f.endTime.value + ':00';
		} else {
			endTime = moment(this.f.endTime.value).format('THH:mm:00');
		}

		this.saving = true;
		const permissionToSend = { ...this.permission };
		permissionToSend.Name = this.permissionForm.value.name;
		permissionToSend.Email = this.permissionForm.value.email;
		permissionToSend.Description = this.permissionForm.value.description != null ? this.permissionForm.value.description : '';
		permissionToSend.CustomerLanguage = this.permissionForm.value.language;
		const timezone = this.getPermissionTimezone();
		permissionToSend.Tz = timezone;
		permissionToSend.Start = this.combineDateTimeInTimezone(startDate, startTime, timezone);
		permissionToSend.End = this.combineDateTimeInTimezone(endDate, endTime, timezone);
		permissionToSend.RfidQeCode = this.permissionForm.value.rfid;
		permissionToSend.PinCode = this.permissionForm.value.pincode;
		permissionToSend.RandomPincode = this.permissionForm.value.pincodeRandom;
		permissionToSend.SendPincodeRbe = this.permissionForm.value.sendPincodeRbe;
		permissionToSend.SendPincodeMtma = this.permissionForm.value.sendPincodeMtma;
		permissionToSend.SendRfidRbe = this.permissionForm.value.sendTrasponderRbe;
		permissionToSend.SendRfidMtma = this.permissionForm.value.sendTrasponderMtma;
		permissionToSend.SendMail = this.permissionForm.value.sendMail;
		permissionToSend.SendAccessMail = this.permissionForm.value.sendAccessMail;

		if (permissionToSend.SendAccessMail === undefined) { // could be undefined if SendMail is false
			permissionToSend.SendAccessMail = false;
		}

		if (permissionToSend.RandomPincode) {
			permissionToSend.PinCode = null;
		}
		if (this.permissionForm.get('enableRfid')?.value === false) {
			permissionToSend.RfidQeCode = null;
			permissionToSend.SendRfidRbe = false;
			permissionToSend.SendRfidMtma = false;
		}
		// decomment if you restore permission-enablePincodeDevices-switch component
		/* if (this.permissionForm.get('enablePincodeDevices')?.value === false) {
			permissionToSend.SendPincodeRbe = false;
			permissionToSend.SendPincodeMtma = false;
		} */

		if(!this.editMode && !this.isEmailNotEmpty()) { // email is empty
			permissionToSend.SendMail = false;
			permissionToSend.SendAccessMail = false;
		}

		if (!this.editMode && !this.isSelectedMtma()) {
			permissionToSend.SendPincodeMtma = false;
			permissionToSend.SendRfidMtma = false;
		}

		if (!this.editMode && !this.isSelectedRbe()) {
			permissionToSend.SendPincodeRbe = false;
			permissionToSend.SendRfidRbe = false;
		}

		if (this.editMode) { // from GET
			if (permissionToSend.Email === undefined) { // is empty and not editable
				permissionToSend.Email = this.permission.Email;
			}
			permissionToSend.RandomPincode = this.permission.RandomPincode; // no change
			permissionToSend.PinCode = this.permission.PinCode; // no change
			permissionToSend.SendMail = this.permission.SendMail; // no change
			permissionToSend.SendAccessMail = this.permission.SendAccessMail; // no change
			if (permissionToSend.SendPincodeRbe === undefined) { // is disabled true, no change
				permissionToSend.SendPincodeRbe = this.permission.SendPincodeRbe;
			}
			if (permissionToSend.SendPincodeMtma === undefined) { // is disabled true, no change
				permissionToSend.SendPincodeMtma = this.permission.SendPincodeMtma;
			}
			if (permissionToSend.SendRfidRbe === undefined) { // is disabled true, no change
				permissionToSend.SendRfidRbe = this.permission.SendRfidRbe;
			}
			if (permissionToSend.RfidQeCode === undefined) { // is disabled with old value, no change
				permissionToSend.RfidQeCode = this.permission.RfidQeCode;
			}
			if (permissionToSend.SendRfidMtma === undefined) { // is disabled true, no change
				permissionToSend.SendRfidMtma = this.permission.SendRfidMtma;
			}
			if (permissionToSend.SendRfidRbe === undefined) { // is disabled true, no change
				permissionToSend.SendRfidRbe = this.permission.SendRfidRbe;
			}
		}

		// we dont send this values
		permissionToSend.PinVisible = undefined;
		permissionToSend.EnablePincodeDevices = undefined;
		permissionToSend.EnableRfid = undefined;
		this.cleanUndefinedValue(permissionToSend);

		if (permissionToSend.Id) { // edit permission
			this.store.dispatch(PermissionActions.editPermission({
				permission: permissionToSend,
				permissionCommands: this.f.selCommands.value,
				permissionThs: this.f.selThs.value,
			}));
		} else { // add permission
			if (this.f.selCommands.value === '') {
				this.f.selCommands.value = [];
			}
			if (this.f.selThs.value === '') {
				this.f.selThs.value = [];
			}
			this.store.dispatch(PermissionActions.addPermission({
				permission: permissionToSend,
				permissionCommands: this.f.selCommands.value,
				permissionThs: this.f.selThs.value,
			}));
		}
	}

	cleanUndefinedValue(obj: Permission) {
		Object.keys(obj).forEach(key => obj[key] === undefined ? delete obj[key] : {});
	}

	filterTimezones(): string[] {
		if (!this.timezones) {
			return [];
		}
		// get the search keyword
		let search = this.timezoneFilterCtrl.value;
		if (!search) {
			this.filteredTimezones.next(this.timezones.slice());
			return [];
		} else {
			search = search.toLowerCase();
			return [];
		}
		// filter timezones
		/* this.filteredTimezones.next(
			this.timezones.filter(zone => zone.toLowerCase().indexOf(search) > -1)
		);
		return []; */
	}

	getTime(date: Date): string {
		return moment(date).format('HH:mm');
	}

	private getPermissionTimezone(): string {
		return this.editMode
			? this.permission.Tz
			: this.currentSite?.Timezone;
	}

	// combine date and time in the given timezone
	private combineDateTimeInTimezone(date: string, time: string, timeZone: string): Date {
		const [year, month, day] = date.split('-').map(Number);
		const [hour, minute, second] = time.replace(/^T/, '').split(':').map(Number);
		// wallAsUtc: wall time (tempo effettivo) as UTC
		const wallAsUtc = Date.UTC(year, month - 1, day, hour, minute, second || 0);
		if (!timeZone) {
			return new Date(wallAsUtc);
		}

		let instant = wallAsUtc;
		for (let i = 0; i < 2; i++) {
			// offset: offset between the wall time and the local time in the given timezone
			const offset = this.getTimeZoneOffsetMs(new Date(instant), timeZone);
			instant = wallAsUtc - offset;
		}
		return new Date(instant);
	}

	// parse date and time from the given timezone
	private parseDateTimeFromTimezone(value: Date | string, timeZone: string): Date {
		if (!value) {
			return value as Date;
		}
		if (!timeZone) {
			const utc = moment.utc(value);
			return new Date(utc.year(), utc.month(), utc.date(), utc.hour(), utc.minute(), utc.second());
		}

		const parts = new Intl.DateTimeFormat('en-US', {
			timeZone,
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit',
			hourCycle: 'h23',
		}).formatToParts(new Date(value));
		const get = (type: string) => Number(parts.find(part => part.type === type)?.value);
		return new Date(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
	}

	private getTimeZoneOffsetMs(date: Date, timeZone: string): number {
		const parts = new Intl.DateTimeFormat('en-US', {
			timeZone,
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit',
			hourCycle: 'h23',
		}).formatToParts(date);
		const get = (type: string) => Number(parts.find(part => part.type === type)?.value);
		const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
		return asUtc - date.getTime();
	}

	setAccessMailValue(event: CheckboxChangeEvent) {
		const value = event.checked;
		if (!value) {
			this.permissionForm.controls['sendAccessMail'].disable();
		} else {
			this.permissionForm.controls['sendAccessMail'].enable();
		}
	}

	getColorFirstTab(): string {
		this.canSwitchTab();
		return StandardNewValidatorStepTabs.getColorFirstTab();
	}

	getColorSecondTab(): string {
		return StandardNewValidatorStepTabs.getColorSecondTab(this.activeIndex);
	}

	getColorThirdTab(): string {
		return StandardNewValidatorStepTabs.getColorThirdTab(this.activeIndex);
	}

	closeDialog(): void {
		this.ref.close();
	}

	isMTMAConnect(modelId: number): boolean {
		if (modelId === undefined || modelId === null) {
			return false;
		}
		return isMtmacModel(modelId);
	}

	isRbe24230(modelId: number): boolean {
		if (modelId === undefined || modelId === null) {
			return false;
		}
		return isRbe24230(modelId);
	}

	isMultiIo(modelId: number): boolean {
		if (modelId === undefined || modelId === null) {
			return false;
		}
		return isMultiIo(modelId);
	}

	checkEditableMailFlags(): void {
		// edit: we can't change flags mail (we don't send any other mails)
		if (this.editMode) { // from GET
			this.f.sendMail.disable();
			this.f.sendAccessMail.disable();
		}
	}

	onSelectedPincodeRandom(): void {
		if (this.f.pincodeRandom.value) { // adding switch on
			this.f.pincode.value = null;
			this.f.pincode.clearValidators();
		} else { // adding switch off
			this.f.pincode.value = this.permission.PinCode;
			this.f.pincode.setValidators([Validators.pattern(/^[0-9]+$/), Validators.required]);
		}
		this.f.pincode.updateValueAndValidity();
		// edit: we can't change pincode
		if (this.editMode) {
			this.f.pincodeRandom.disable();
			this.f.pincode.disable();
		}
	}

	onSelectedEnablePincodeToDevice(): void {
		// decomment if you restore permission-enablePincodeDevices-switch component
		/* if (!this.f.enablePincodeDevices.value) { // adding switch off
			this.permissionForm.controls['sendPincodeRbe'].setValue(false);
			this.permissionForm.controls['sendPincodeMtma'].setValue(false);
			this.f.sendPincodeRbe.disable();
			this.f.sendPincodeMtma.disable();
		} else {                                  // adding and editing switch on
			this.permissionForm.controls['sendPincodeRbe'].setValue(true);
			this.permissionForm.controls['sendPincodeMtma'].setValue(true);
			this.f.sendPincodeRbe.enable();
			this.f.sendPincodeMtma.enable();
		} */
		// edit: we can't change true flags
		if (this.editMode && (this.permission.SendPincodeRbe || this.permission.SendPincodeMtma)) { // from GET
			this.f.enablePincodeDevices.disable();
			if (this.permission.SendPincodeRbe) {
				this.f.sendPincodeRbe.disable();
			} else { // default value : getPermission value
				this.f.sendPincodeRbe.value = this.permission.SendPincodeRbe;
			}
			if (this.permission.SendPincodeMtma) {
				this.f.sendPincodeMtma.disable();
			} else { // default value : getPermission value
				this.f.sendPincodeMtma.value = this.permission.SendPincodeMtma;
			}
		}
	}

	onSelectedEnableTagcodeToDevice(): void {
		if (!this.f.enableRfid.value) { // adding switch off
			this.f.rfid.value = null;
			this.f.rfid.clearValidators();
			this.f.sendTrasponderRbe.value = false;
			this.f.sendTrasponderMtma.value = false;
		} else { // adding and editing switch on
			this.f.rfid.setValidators(Validators.required);
			this.f.sendTrasponderRbe.value = true;
			this.f.sendTrasponderMtma.value = true;
		}
		this.f.rfid.updateValueAndValidity();
		// edit: we can't change true flags
		if (this.editMode && (this.permission.SendRfidRbe || this.permission.SendRfidMtma)) { // from GET
			this.f.enableRfid.disable();
			this.f.rfid.disable();
			if (this.permission.SendRfidRbe) {
				this.f.sendTrasponderRbe.disable();
			} else { // default value : getPermission value
				this.f.sendTrasponderRbe.value = this.permission.SendRfidRbe;
			}
			if (this.permission.SendRfidMtma) {
				this.f.sendTrasponderMtma.disable();
			} else { // default value : getPermission value
				this.f.sendTrasponderMtma.value = this.permission.SendRfidMtma;
			}
		}
	}

	isEmailNotEmpty(): boolean {
		if (this.f !== undefined && this.f.email.value !== undefined && this.f.email.value !== null && this.f.email.value !== '') {
			return true;
		}
		return false;
	}

	isSelectedMtma(): boolean {
		if (this.f !== undefined && this.f.selCommands.value !== undefined && this.f.selCommands.value.length > 0) {
			for (const c of this.f.selCommands.value) {
				const device = this.currentSite.Devices.find(dev => dev.Id === c.DeviceId);
				if (device === undefined) {
					continue;
				}
				const deviceRoot = this.currentSite.Devices.find(dev => dev.Id === device.ParentId);
				if (deviceRoot !== undefined && this.isMTMAConnect(deviceRoot.ModelId) && this.isMultiIo(device.ModelId)) {
					return true;
				}
			}
		}
		return false;
	}

	isSelectedRbe(): boolean {
		if (this.f !== undefined && this.f.selCommands.value !== undefined && this.f.selCommands.value.length > 0) {
			for (const c of this.f.selCommands.value) {
				const device = this.currentSite.Devices.find(dev => dev.Id === c.DeviceId);
				if (device !== undefined && this.isRbe24230(device.ModelId)) {
					return true;
				}
			}
		}
		return false;
	}

	getCurrentLanguageFlag(customerLanguage): string {
		const currentLangFlagFile = this.flagImagePath + customerLanguage + this.flagImageFormat;
		return currentLangFlagFile;
	}

	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	col6Responsive(): string {
		return StandardPageLayout.col6Responsive(this.screenWidth);
	}

	col6ResponsiveNoPadding(): string {
		return StandardPageLayout.col6ResponsiveNoPadding(this.screenWidth);
	}

	dynamicDatepicker(): string {
		return StandardDynamicComponent.dynamicDatepicker(this.screenWidth);
	}

	dynamicAutocomplete(): string {
		return StandardDynamicComponent.dynamicAutocomplete(this.screenWidth);
	}

	dynamicInputShortTextWidth(): string {
		return StandardPageLayout.dynamicInputTextDesktopTabletWidth(this.screenWidth, 'w-20rem', 'w-20rem');
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-30rem');
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	justifyContentStyle(): string {
		return StandardNewValidator.justifyMessagesNewValidator(this.screenWidth);
	}

	dynamicInputTextFormat(classType: string): string {
		return StandardPageLayout.simpleInputTextLayout(classType, this.screenWidth);
	}

	newShowErrors(type: PermissionDialogComponent["f"], classType: string) {
		return StandardNewValidator.newShowErrors(type, classType, this.screenWidth);
	}

	showErrorRequiredMessage(type: PermissionDialogComponent['f']): boolean {
		return StandardNewValidator.showErrorRequiredMessage(type);
	}

	customPaddingX(): string {
		return StandardPageLayout.customPaddingX(this.screenWidth);
	}
}
